import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { DashboardPinIcon } from '../assets/dashboard/DashboardPinIcon';
import { ReactComponent as FlipIcon } from '../assets/tools/icon-flip_projec-active.svg';
import { ReactComponent as ArvIcon } from '../assets/tools/icon-arv_analysis_active.svg';
import { ReactComponent as ListedSaleIcon } from '../assets/dashboard/listed-for-sale.svg';
import { ReactComponent as ListingRemovedIcon } from '../assets/dashboard/listing-removed.svg';
import { ReactComponent as SoldIcon } from '../assets/dashboard/sold.svg';
import { ReactComponent as ListedRentIcon } from '../assets/dashboard/listed-for-rent.svg';
import { ReactComponent as IncreaseIcon } from '../assets/dashboard/price-increased.svg';
import { ReactComponent as DecreaseIcon } from '../assets/dashboard/price-decreased.svg';
import AppNavbar from '../features/AppNavbar';
import PageShell from '../layouts/PageShell';
import BackLink from '../components/BackLink';
import { reilmAPI } from '../services/apiClient';
import { getIn } from './getIn';
import { parseCalendarDate } from '../features/arv/calendarDate';

const PROPERTY_TYPE_LABELS = {
  single_family: 'Single Family',
  multi_family: 'Multi Family',
  condo: 'Condo',
  townhouse: 'Townhouse',
};

// Same bounds as reilm-be's RECOMPUTE_CRITERIA_LIMITS and fieldLimits.js's beds/baths/sqft entries,
// kept local: those entries carry Arv Search Criteria's message/zeroAsUnset too. These feed calcScore/compAdjustments
// directly whenever arv.criteria's own min is empty, so an unbounded typo here (18000 instead of
// 1800) reaches the ARV estimate unchecked.
const BEDS_FIELD_MIN = 0;
const BEDS_FIELD_MAX = 15;
const BATHS_FIELD_MIN = 0;
const BATHS_FIELD_MAX = 15;
const SQFT_FIELD_MIN = 0;
const SQFT_FIELD_MAX = 10000;

const STATUS_ICON_MAP = {
  'Listed':     ListedSaleIcon,
  'Listing removed': ListingRemovedIcon,
  'Sold':            SoldIcon,
  "Listed for rent":     ListedRentIcon,
  price_increase:  IncreaseIcon,
  price_decrease:  DecreaseIcon,
};

const STATUS_LABEL_MAP = {
  listed_sale:     'Listed for Sale',
  listing_removed: 'Withdrawn',
  sold:            'Sold',
  listed_rent:     'Listed for Rent',
  price_increase:  'Price Increased',
  price_decrease:  'Price Decreased',
};

// The three maps above are keyed on entry.event_name, and only STATUS_ICON_MAP survives that. Its
// first four keys are the display strings the provider actually writes, so 312 of the 396 events in
// the live collection do render an icon. The other two keys, and every key in the two maps below,
// are internal codes the provider never writes. Census over the live collection, 30.08, 396 events
// across 37 properties: Sold 117, Listing removed 83, Listed 69, Price Changed 66, Listed for rent
// 43, Price Changed for rent 12, Relisted 6. So STATUS_LABEL_MAP is dead outright, and the price
// pair is unreachable in all three maps - defect 24, inherited from 45080a2. Re-keying is mechanical
// everywhere except that pair: the provider says only "Price Changed", so up-versus-down has to come
// from consecutive prices. pages/ListingDetails.jsx already re-keyed its own copies.
//
// STATUS_BADGE_BG is dead for a second, independent reason, and that one no re-keying fixes. Every
// one of the six assets these rows render opens with a 20x20 fill circle - rect 0.5/0.5, 19x19,
// rx 9.5 - exactly coincident with the size-[20px] rounded-full wrapper this map colours. The icon
// covers the background in every branch, on both screens. ListingDetails is the proof: it has two
// live keys and its two values match its assets' own circle colours, which is why nobody noticed
// they were doing nothing. So these six values are decoration, and this map is left as it was rather
// than "corrected" - a mutation flipping the price pair leaves all 58 tests green, because there is
// nothing to see.
//
// That matters because the design does disagree about those two circles, and the fix is an asset,
// not a value here. Measured in files/sow2-section-lean.svg, the only export on disk whose ids
// resolve: the frame named `history` - a section-level sibling of `property-dashboard` drawn inside
// its own column, x 5247-5905 against the screen's 5243-6415 - fills and strokes the badge #C6CFC6
// on five instances, while comp-details/history_2 and grid-expanded/history_3 use #CBDCC3 on four.
// price-increased.svg and price-decreased.svg carry #CBDCC3 and are imported by both this file and
// ListingDetails.jsx, so they are already right for one screen and wrong for the other, and no
// single value in them can be right for both. Defect 26; the remedy is a per-screen variant.
//
// #CBDCC3 is brand.rowBorder (the History row divider below).
const STATUS_BADGE_BG = {
  listed_sale:     '#D85263',
  listing_removed: null,
  sold:            null,
  listed_rent:     '#F5C312',
  price_increase:  '#CBDCC3',
  price_decrease:  '#CBDCC3',
};


// Color scale matches assets/arv/school-ratings-color.svg
// 0=gray · 1–4=mid-green · 5–7=dark-green · 8–10=brand-dark
const getSchoolRatingColor = (rate) => {
  const n = parseInt(rate, 10);
  if (!n || n <= 0) return '#C4CCBE';
  if (n <= 4)       return '#4F7A35';
  if (n <= 7)       return '#2F4A1F';
  return '#17270D';
};

// "Apr 25, 2024 9:42 AM" as the quick-actions frame draws it: no comma before the time, which a single
// toLocaleString call always inserts.
const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const date = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${date} ${time}`;
};

const formatHistoryDate = (iso) => {
  if (!iso) return '';
  return parseCalendarDate(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatPrice = (price) => {
  if (!price && price !== 0) return '';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(price);
};

// Sqft shows "1,500" (A3 Q2), so it is a text input. Parsing tolerates what people type around a
// number ("1 500", "1500.", "1500 sqft"); anything else is NaN, never null, so it cannot read as
// "the field was emptied".
const toStoredSqft = (value) => (value == null || value === '' ? null : Number(value));
const formatSqft = (value) => {
  const n = toStoredSqft(value);
  if (n == null) return '';
  return Number.isFinite(n) ? n.toLocaleString('en-US') : String(value);
};
const parseSqft = (text) => {
  const typed = String(text).trim();
  if (typed === '') return null;
  const raw = typed.replace(/sqft$/i, '').replace(/[\s,]/g, '').replace(/\.$/, '');
  return /^\d+(\.\d+)?$/.test(raw) ? Number(raw) : NaN;
};

// Merged "Annual Property Taxes" (A3 c): read-only like the two fields it replaces. A year with no
// amount says nothing; a stored 0 is data (formatPrice(0) is "$0").
const formatTax = (tax) => {
  const amount = tax?.amount != null ? formatPrice(tax.amount) : '';
  if (amount === '') return null;
  const year = tax?.year;
  return year != null && String(year).trim() !== '' ? `${amount} (${year})` : amount;
};

// details.sewer is written '' at creation; the provider value sits in amenities.utilities as
// "Sewer: Public Sewer". Render-only, nothing is written back (A3 Q6: frame shows "Public").
const sewerValue = (details) => {
  const own = typeof details?.sewer === 'string' ? details.sewer.trim() : '';
  const utilities = details?.amenities?.utilities;
  const list = Array.isArray(utilities) ? utilities : (typeof utilities === 'string' ? [utilities] : []);
  const fromProvider = list.find(u => typeof u === 'string' && /^sewer:/i.test(u.trim()));
  const raw = own || (fromProvider ? fromProvider.trim().replace(/^sewer:\s*/i, '') : '');
  return raw.replace(/\s+sewer$/i, '').trim() || null;
};

// px 4, not 8: "10,000" needs ~46 px of text in the drawn 55-px box.
const inputCls = 'w-full h-[40px] text-[14px] font-medium text-center border border-brand-fieldBdr rounded-[4px] px-[4px] py-[8px] bg-white outline-none focus:border-brand-green hover:border-brand-green';
// 29.06 comment, pt. 3: "field labels (not values)" -> #4F5F4A. Size 12px per spec/property-dashboard.md,
// "Підпис поля в деталях". `uppercase` dropped 26.08, second review pass: the export renders mixed
// case ("Lot Size", "Flood Zone"), measured on node property-dashboard/details/Frame 327 — the
// pre-26.08 uppercase was a guess, same category of mistake as the font-bold guess below.
// A3 c: 2 px from label to value box (6 for the school badges), line 16.
const labelCls = 'block text-[12px] leading-[16px] text-brand-secondary tracking-wide mb-[2px]';
const labelSchoolCls = 'block text-[12px] leading-[16px] text-brand-secondary tracking-wide mb-[6px]';
// Same comment, pt. 3: "Section headers" -> #4F5F4A, mixed case. Since A3 the only one is
// "Community", 16 above the next label.
// 14px/800, re-sampled 02.09 from sow2-section-lean.svg (09.08) — nodes details/Frame 327/Frame 595
// (Community, Schools, Taxes) and quick-actions/Frame 606/Frame 325_6/Frame 323 (Major Systems).
// The 26.08 pass read this as regular off figma-18.08/sow2-section-text.18.08.svg, which writes
// `bold` or nothing for the whole 300-900 range; its silence is not 400, and reading a weight from
// it is Дефект 27. Comment Set #1, PD 8, asks for ExtraBold here by name.
const labelSectionCls = 'block text-[14px] leading-[20px] font-extrabold text-brand-secondary tracking-wide mb-[16px]';

// Detail-field value and its empty state, both 14px — this screen's value size, unlike
// AddOpportunity's 13px. These fields carry no onChange/save (pure display of `details.*`), so
// unlike AddOpportunity's <input placeholder="—"> (mr-04) there is no save path for the dash to
// reach in the first place; FieldValue below is the read-only shape of the same rule.
// The weights came from spec/property-dashboard.md, which read both as regular off the 18.08
// export — Дефект 27 again. Re-sampled 02.09 from sow2-section-lean.svg and corrected below.
const EM_DASH = '—';
// 14px/500, sow2-section-lean.svg node details/Frame 327/.../input_114. The empty state keeps
// regular: the dash is drawn 14/regular #8A9A85 on the same nodes, a different weight from a value
// that is actually there.
const valueCls = 'text-[14px] leading-[18px] font-medium text-brand-dark break-words';
const emptyValueCls = 'text-[14px] leading-[18px] text-brand-placeholder';
const FieldValue = ({ value }) => {
  // A bare `value === ''` misses a whitespace-only string ('   ') — it is not `===` to '', so it
  // took the filled branch and rendered as data. `typeof value === 'string'` guards the trim so a
  // legitimate 0 (Lot Size, Total Tax) never goes through String coercion and is never mistaken for
  // empty.
  const isEmpty = value === null || value === undefined || (typeof value === 'string' && value.trim() === '');
  return <p className={isEmpty ? emptyValueCls : valueCls}>{isEmpty ? EM_DASH : value}</p>;
};

// Tool buttons: SOW 3 section 4, restyled by SOW 4 A3 b (frame property-dashboard/Frame 313), ARV left.
const ANALYSIS_TOOLS = [
  { route: 'arv', title: 'ARV Analysis', description: 'Estimate value with comps', Icon: ArvIcon, background: '#18290E' },
  { route: 'flip', title: 'Flip Analysis', description: 'Analyze flips, new construction, and wholesale deals', Icon: FlipIcon, background: '#17270D' },
];

// The Link is the 72-px box (min-h so a wrapped description at 375 does not clip). The 5% white
// overlay is a background-image so it paints under the border too (a child stops at the padding box
// and left a darker 1-px rim at rest). The transparent 1-px border is the hover border at rest, so px 15 / glow 155,65 inside it land the drawn +16 and
// (+182,+92) - r 26 from the outer edge. Path stroke 1 is the drawn 1.5 on a 24-box glyph shown at 36.
const ToolCard = ({ tool, to, state }) => {
  const { Icon, title, description, background } = tool;
  return (
    <Link
      to={to}
      state={state}
      className="relative overflow-hidden rounded-[12px] min-h-[72px] px-[15px] flex items-center gap-[10px] border border-transparent cursor-pointer transition hover:border-brand-green hover:-translate-y-[2px] outline-none focus-visible:ring-2 focus-visible:ring-brand-green bg-[linear-gradient(rgba(255,255,255,0.05),rgba(255,255,255,0.05))]"
      style={{ backgroundColor: background }}
    >
      <Icon className="[&_path]:[stroke-width:1]" style={{ width: 36, height: 36, flexShrink: 0 }} />
      <div className="min-w-0">
        <p className="text-white font-extrabold text-[16px] leading-[16px]">{title}</p>
        <p className="text-white text-[14px] leading-[16px]">{description}</p>
      </div>
      <div
        data-testid="tool-card-glow"
        aria-hidden="true"
        className="absolute rounded-full pointer-events-none"
        style={{ left: 155, top: 65, width: 52, height: 52, background: '#4CBC18', opacity: 0.7, filter: 'blur(45.65px)' }}
      />
    </Link>
  );
};

// A3 c: each tab 40 tall, text line 20 at +10, the 2-px underline at the bottom.
const tabStyle = (active) => ({
  paddingTop: 10,
  paddingBottom: 8,
  lineHeight: '20px',
  borderBottom: active ? '2px solid #4CBC18' : '2px solid transparent',
  // 29.06 comment, pt. 3: "Property Details/History when not selected" -> #4F5F4A. The underline
  // and the colour are the only difference between the two states; both carry the same weight.
  // 16/800 on details/Frame 310/Frame 337 in sow2-section-lean.svg (09.08). The 26.08 pass read
  // 400 off the 18.08 export, which cannot express a weight other than bold — Дефект 27. Comment
  // Set #1, PD 8, names both tabs.
  color: active ? '#17270D' : '#4F5F4A',
  fontSize: 16,
  fontWeight: 800,
  background: 'none',
  cursor: 'pointer',
  outline: 'none',
  whiteSpace: 'nowrap',
});

const PropertyDashboard = () => {
  const { id } = useParams();

  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  // Distinguishes "the record is gone" (404) from everything else a fetch can do -- a stubbed 500
  // used to render the same "Property not found." as a real 404, and so would a timeout or a
  // network drop. null means no failure recorded (still loading, or a 401 the interceptor is
  // already handling). 'locked' is its own third value, not folded into null: a 403 subscription
  // refusal is not "no failure recorded" (the fetch really did fail, and the record is genuinely not
  // shown), it just needs a render distinct from both of the other two -- see the `!property` branch
  // below.
  const [loadError, setLoadError] = useState(null);
  const [historyTab, setHistoryTab] = useState(false);

  const [beds, setBeds] = useState('');
  const [baths, setBaths] = useState('');
  const [sqft, setSqft] = useState('');
  const [notes, setNotes] = useState('');
  const [notesModified, setNotesModified] = useState(null);
  const [pinned, setPinned] = useState(false);
  // Shared banner across all five save call sites (pin, beds, baths, sqft, notes) rather than
  // one per field -- see src/pages/README.md for the full mechanism.
  const [saveError, setSaveError] = useState(null);
  // Which call site currently owns the banner, keyed by patch shape so an unrelated field's
  // settlement cannot clear or raise this one's message. See src/pages/README.md.
  const failedSavesRef = useRef(new Set());
  // Per-key generation counter gating a stale settlement (issued earlier, resolving later) from
  // acting on a key a newer save already owns. Ported from Arv.jsx's own field of the same name;
  // the race and the id-boundary trace both live in src/pages/README.md.
  const saveGenerationsRef = useRef(new Map());
  // Best estimate (not proof) of what the server holds for a key -- read from the PATCH response
  // body (getIn, f-9), sent value as fallback, on every settled response including a stale one.
  // Read by the pin rollback and by Sqft's blur. Full reasoning in src/pages/README.md.
  const confirmedSavesRef = useRef(new Map());

  const nextSaveGeneration = (key) => {
    const gen = (saveGenerationsRef.current.get(key) ?? 0) + 1;
    saveGenerationsRef.current.set(key, gen);
    return gen;
  };

  const isCurrentSave = (key, gen) => saveGenerationsRef.current.get(key) === gen;

  // Fresh instance per id (App.js's PropertyDashboardRoute key={id}); in a normal mount this effect
  // runs once, so the removed setLoading/setLoadError/setSaveError calls here only restated each
  // state's own initializer. Losing that key reopens the confirmed-value leak and stale loading/error state; see src/pages/README.md.
  useEffect(() => {
    let cancelled = false;
    reilmAPI.getProperty(id)
      .then(({ data }) => {
        if (cancelled) return;
        const p = data.data.property;
        setProperty(p);
        setBeds(p.details?.beds ?? '');
        setBaths(p.details?.baths ?? '');
        setSqft(formatSqft(p.details?.sqft));
        // Sqft's blur compares against this (a save refreshes it from the response), so an
        // unchanged blur writes nothing and an invalid one restores the stored value.
        confirmedSavesRef.current.set('details.sqft', toStoredSqft(p.details?.sqft));
        setNotes(p.notes?.text ?? '');
        setNotesModified(p.notes?.updatedAt ?? null);
        setPinned(p.pin ?? false);
        // Seeds the pin rollback target with the authoritative loaded value -- simpler than Arv's
        // lazy per-row seed (one row toggled among many): pin always has a known value at load.
        confirmedSavesRef.current.set('pin', p.pin ?? false);
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        // Same guard as AddOpportunity's handleAddAndOpen/onAddressChange: the interceptor in
        // services/config.js is already redirecting to #/login on a failed refresh, so setting a
        // message here would flash the wrong cause on the way out.
        if (status === 401) return;
        const code = err?.response?.data?.error?.code;
        // Same reasoning as the 401 carve-out above, for the other case config.js already handles:
        // a 403 with one of these two codes fires reilm:subscription-expired, and ExpiredModal
        // (features/subscription) renders over whatever is on screen. Neither "Property not found."
        // nor "Could not load this property, try again" fits -- the record is present and readable,
        // and a retry cannot succeed until the trial or subscription itself is fixed -- so this is
        // its own loadError value rather than an early return into the null/still-loading default:
        // the `!property` branch below renders no message for it, on purpose, rather than falling
        // through to either one. Any other 403 code (a real per-property authorization refusal)
        // still falls through to load_failed below.
        if (status === 403 && (code === 'TRIAL_EXPIRED' || code === 'SUBSCRIPTION_REQUIRED')) {
          setLoadError('locked');
          return;
        }
        // Only a 404 means the record itself is gone. Everything else used to fall through to the
        // same "Property not found." render regardless of what actually happened.
        setLoadError(status === 404 ? 'not_found' : 'load_failed');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  // save() used to be `(patch) => reilmAPI...(id, patch).catch(() => {})` -- a failed PATCH vanished
  // silently, the typed value stayed on screen, and notesModified below advanced regardless of the
  // outcome, so a failed save was indistinguishable from a successful one. Returning the outcome
  // (rather than throwing) lets a caller that needs to sequence on it -- only the notes field does --
  // do so, without every other call site needing its own catch.
  const save = (patch) => {
    // Every call site sends exactly one distinct field, so the patch's own key set is a stable
    // identity for "which control is this" without threading a name through every call site.
    const key = Object.keys(patch).join(',');
    // Taken synchronously before the request goes out -- two blurs on the same key in quick
    // succession must not share a generation, or the second could never be told apart from the
    // first once both are in flight. Same shape as Arv.jsx's persistCriteriaFilters.
    const gen = nextSaveGeneration(key);
    return reilmAPI.updatedPropertiesDetails(id, patch)
      .then((res) => {
        // Unconditional, even when this settlement is stale below -- see confirmedSavesRef's own
        // comment. Reads the response body via a dotted-path getter (the response nests
        // 'details.beds' as property.details.beds, not property['details.beds']), sent value as
        // fallback if the path is absent -- README.md, "confirmed value reads the response".
        const saved = getIn(res?.data?.data?.property, key);
        confirmedSavesRef.current.set(key, saved ?? patch[key]);
        // Stale settlement resolves undefined, distinct from true/false -- full reasoning (why
        // failedSavesRef must not be touched here) in src/pages/README.md.
        if (!isCurrentSave(key, gen)) return undefined;
        failedSavesRef.current.delete(key);
        // Only clear the shared banner once nothing is left failing -- a same-field retry deletes
        // the only entry and clears it same as before; a different field resolving while another
        // field's failure is still outstanding leaves that entry in place and the banner up.
        if (failedSavesRef.current.size === 0) setSaveError(null);
        return true;
      })
      .catch((err) => {
        // Same generation gate as the success branch -- a stale failure must not raise the banner
        // over a key a newer save (which may itself still succeed) already owns.
        if (!isCurrentSave(key, gen)) return undefined;
        if (err?.response?.status === 401) return false; // interceptor already redirecting to login
        const code = err?.response?.data?.error?.code;
        // Same carve-out as the load path above: config.js already dispatches
        // reilm:subscription-expired for these two codes and ExpiredModal renders over the screen,
        // so this field is not "failing" in the sense the banner exists for and must not be added
        // to failedSavesRef -- an entry that nothing else will ever remove would keep the banner
        // stuck up after the real failure it was covering had already cleared.
        if (err?.response?.status === 403 && (code === 'TRIAL_EXPIRED' || code === 'SUBSCRIPTION_REQUIRED')) {
          return false;
        }
        failedSavesRef.current.add(key);
        setSaveError("We couldn't save your changes. Please try again.");
        return false;
      });
  };

  const addressDisplay = property ? [
    property.address?.street,
    property.address?.city,
    property.address?.state,
    property.address?.zip,
  ].filter(Boolean).join(', ') : '';

  const groupedHistory = {};
  (property?.history ?? []).forEach(entry => {
    const year = entry.date ? parseCalendarDate(entry.date).getFullYear() : 'Unknown';
    if (!groupedHistory[year]) groupedHistory[year] = [];
    groupedHistory[year].push(entry);
  });
  const sortedYears = Object.keys(groupedHistory).sort((a, b) => b - a);

  if (loading) {
    return (
      <PageShell>
        <AppNavbar />
        <div className="flex items-center justify-center py-20 text-brand-muted text-[20px]">Loading…</div>
      </PageShell>
    );
  }

  if (!property) {
    // Locked renders no message at all -- ExpiredModal (mounted at App level, above this component)
    // already states the cause and carries the only useful action. Pipeline's own locked state
    // (pages/Pipeline.jsx) shows its own card repeating the same fact under the same modal; that is
    // a duplication this screen does not need to also carry. AppNavbar stays, same as Pipeline, so
    // closing the modal does not strand the user on a page with no way out.
    if (loadError === 'locked') {
      return (
        <PageShell>
          <AppNavbar />
        </PageShell>
      );
    }
    return (
      <PageShell>
        <AppNavbar />
        <div className="flex items-center justify-center py-20 text-red-400 text-[14px]">
          {loadError === 'load_failed' ? 'Could not load this property, try again' : 'Property not found.'}
        </div>
      </PageShell>
    );
  }

  const details = property.details ?? {};
  const schools = details.schools ?? {};
  const hoaArray    = details.amenities?.homeowners_association ?? []
  const hoaFeeEntry  = hoaArray.find(s => s.startsWith('Association Fee:'))
  const hoaFreqEntry = hoaArray.find(s => s.startsWith('Association Fee Frequency:'))
  const hoaFeeAmt    = hoaFeeEntry  ? Number(hoaFeeEntry.split(':')[1]?.trim())  : null
  const hoaFreq      = hoaFreqEntry ? hoaFreqEntry.split(':')[1]?.trim()         : null
  const hoaAmenities = details.amenities?.amenities_and_community_features?.[0] ?? null
  // hoaAmenities != null alone is not the gate: mapApiToForm (AddOpportunity) sets
  // details.amenities to an object for any address it resolved at all, so the object is truthy and
  // the amenities-text branch always runs; typing into that field and clearing it before save writes
  // [''] back, not an absent key or []. '' != null is true, so the section rendered on that stored
  // shape with two em dashes — exactly the "positive claim of no data" the gate above exists to
  // avoid. trim(), not a bare !== '', for the same reason FieldValue's own isEmpty trims below: a
  // whitespace-only entry is the same stored shape as '' and the same case the 26.08 measurement
  // reports as one class ("порожній або пробільний рядок"), so a bare inequality would leave this
  // gate and FieldValue disagreeing on the same string one field apart. Live measurement 26.08: 42
  // objects carry the amenities container, 5 have a real value here, 0 have an empty/whitespace
  // string today, 37 have no key — so this branch is unreached in production right now, but the
  // write path is open and it is the same category of guard as Lot Size below.
  const hasHoa       = (hoaFeeAmt != null && hoaFeeAmt > 0) || (hoaAmenities != null && String(hoaAmenities).trim() !== '')
  const hasSchools   = Boolean(schools.elementary || schools.middle || schools.high)

  return (
    <PageShell>
      <AppNavbar />
      <div className="mt-[24px] flex flex-col gap-y-[24px] pb-[60px]">

        {/* Heading, A3 a: frame property-dashboard/Frame 977_3, 18 + 4 + 32 + 4 + 24 = 82. */}
        <div>
          <div className="flex items-end justify-between gap-x-[16px]">
            <div className="min-w-0">
              <BackLink to="/pipeline">Back to Pipeline</BackLink>
              <h1 className="mt-[4px] text-[24px] leading-[32px] font-extrabold text-brand-dark">Property Dashboard</h1>
              <p className="mt-[4px] text-[18px] leading-[24px] font-normal text-brand-dark break-words">{addressDisplay}</p>
            </div>
            {/* `group` fires the icon's own hover/focus classes (colour change on the SVG nodes);
                the discarded-return-value rollback bug and its fix are in src/pages/README.md. */}
            <button
              onClick={() => {
                const next = !pinned
                setPinned(next)
                save({ pin: next }).then((ok) => {
                  if (ok === false) setPinned(confirmedSavesRef.current.get('pin'))
                })
              }}
              className="group shrink-0"
              title={pinned ? 'Unpin property' : 'Pin property'}
            >
              <DashboardPinIcon active={pinned} />
            </button>
          </div>
          {saveError && (
            <p className="mt-[8px] text-red-400 text-[14px]">{saveError}</p>
          )}
        </div>

        {/* Tool buttons, A3 b: no parent card and no "Analysis Tools" title. */}
        <div className="grid grid-cols-2 gap-[24px]">
          {ANALYSIS_TOOLS.map(tool => (
            <ToolCard
              key={tool.route}
              tool={tool}
              to={`/property/${id}/${tool.route}`}
              state={{ property }}
            />
          ))}
        </div>

        {/* Bottom cards, A3 c/d: 480 tall from the canvas breakpoint, min-height below it so narrow
            widths grow instead of clipping. Paddings are measured from the outer edge, so each is the
            drawn offset minus the 1-px border (Notes: 486 - 2 - 46 = the drawn 438 textarea). */}
        <div className="flex gap-x-[24px] items-stretch">

          {/* Left: Property Details card */}
          <div className="basis-[57.5%] min-w-0 min-h-[480px] canvas:h-[480px] flex flex-col bg-white border border-brand-fieldBdr rounded-[8px] px-[23px] pt-[15px] pb-[31px]">

            {/* Tab bar */}
            <div className="shrink-0 flex items-end gap-x-[32px]">
              <button className='border-b-2 border-brand-green ' style={tabStyle(!historyTab)} onClick={() => setHistoryTab(false)}>
                Property Details
              </button>
              <button style={tabStyle(historyTab)} onClick={() => setHistoryTab(true)}>
                History
              </button>
            </div>

            {/* Property Details tab: frame property-dashboard/details, 3 columns of 198, gap 24. */}
            {!historyTab && (
              <div className="mt-[22px] flex-1 min-h-0 overflow-y-auto">
                <div className="flex flex-col gap-y-[14px]">
                  <div className="grid grid-cols-3 gap-x-[24px]">
                    <div className="flex gap-x-[16px]">
                      <div className="flex-1 min-w-0">
                        <label htmlFor="property-beds" className={labelCls}>Beds</label>
                        <input
                          id="property-beds"
                          type="number"
                          className={inputCls}
                          value={beds}
                          onChange={e => setBeds(e.target.value)}
                          onBlur={() => {
                            // Integer-only, same as fieldLimits.js's beds entry: a fractional value
                            // (a type="number" input with no `step` still accepts 3.5) 400s on the
                            // backend's assertRecomputeCriteriaBody rule with no field-level message,
                            // so it must never reach save() in the first place.
                            const n = Number(beds)
                            const inRange = n >= BEDS_FIELD_MIN && n <= BEDS_FIELD_MAX
                            const valid = beds !== '' && inRange && Number.isInteger(n)
                            if (!valid) setBeds('')
                            save({ 'details.beds': valid ? n : null })
                          }}
                          placeholder="Bd"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <label htmlFor="property-baths" className={labelCls}>Baths</label>
                        <input
                          id="property-baths"
                          type="number"
                          className={inputCls}
                          value={baths}
                          onChange={e => setBaths(e.target.value)}
                          onBlur={() => {
                            const n = Number(baths)
                            const inRange = n >= BATHS_FIELD_MIN && n <= BATHS_FIELD_MAX
                            const valid = baths !== '' && inRange
                            if (!valid) setBaths('')
                            save({ 'details.baths': valid ? n : null })
                          }}
                          placeholder="Ba"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <label htmlFor="property-sqft" className={labelCls}>Sqft</label>
                        <input
                          id="property-sqft"
                          type="text"
                          inputMode="numeric"
                          className={inputCls}
                          value={sqft}
                          onChange={e => setSqft(e.target.value)}
                          onBlur={() => {
                            const stored = toStoredSqft(confirmedSavesRef.current.get('details.sqft'))
                            const n = parseSqft(sqft)
                            const valid = n === null || (n >= SQFT_FIELD_MIN && n <= SQFT_FIELD_MAX)
                            if (!valid) {
                              setSqft(formatSqft(stored))
                              return
                            }
                            setSqft(formatSqft(n))
                            if (n === stored) return
                            save({ 'details.sqft': n })
                          }}
                          placeholder="Sqft"
                        />
                      </div>
                    </div>
                    {/* Row-1 read-only values sit 6 from the top of the 40-px row, not centred. */}
                    <div>
                      <span className={labelCls}>Year Built</span>
                      <div className="pt-[6px]"><FieldValue value={details.yearBuilt} /></div>
                    </div>
                    <div>
                      <span className={labelCls}>Property Type</span>
                      <div className="pt-[6px]"><FieldValue value={PROPERTY_TYPE_LABELS[property.propertyType] ?? property.propertyType} /></div>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-x-[24px]">
                    <div>
                      <span className={labelCls}>Lot Size</span>
                      {/* Three-part guard: != null for the absent shape, trim() for blank strings
                          (Number('') is 0, "0 acres"), Number.isFinite for unparseable text ("NaN
                          acres"). A stored 0 is data. Unit lower-case "acres" per the export. */}
                      <div className="pt-[4px]">
                        <FieldValue value={
                          details.lotSize != null && String(details.lotSize).trim() !== '' && Number.isFinite(Number(details.lotSize))
                            ? `${Number(details.lotSize).toLocaleString()} acres`
                            : null
                        } />
                      </div>
                    </div>
                    <div>
                      <span className={labelCls}>Flood Zone</span>
                      <div className="pt-[4px]"><FieldValue value={details.floodZone} /></div>
                    </div>
                    <div>
                      <span className={labelCls}>Sewer</span>
                      <div className="pt-[4px]"><FieldValue value={sewerValue(details)} /></div>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-x-[24px]">
                    <div>
                      <span className={labelCls}>Annual Property Taxes</span>
                      <div className="pt-[4px]"><FieldValue value={formatTax(details.tax)} /></div>
                    </div>
                  </div>
                </div>

                {/* Community: HOA and schools, each behind its own gate (a fee on 1 of 5042 live
                    objects, amenities on 5, 26.08) so an absent section never reads as "—" rows. */}
                {(hasHoa || hasSchools) && (
                  <div className="mt-[26px]">
                    <p className={labelSectionCls}>Community</p>
                    <div className="flex flex-col gap-y-[22px]">
                      {hasHoa && (
                        <div className="grid grid-cols-3 gap-x-[24px]">
                          <div>
                            <span className={labelCls}>HOA Fee</span>
                            {/* `> 0`: hoaFeeAmt is null with no "Association Fee:" entry and NaN when
                                it does not parse ("$250"); `> 0` excludes both. No live 0 (26.08). */}
                            <div className="pt-[4px]">
                              <FieldValue value={hoaFeeAmt != null && hoaFeeAmt > 0
                                ? `${formatPrice(hoaFeeAmt)}${hoaFreq ? `/${hoaFreq}` : ''}`
                                : null} />
                            </div>
                          </div>
                          <div className="col-span-2">
                            <span className={labelCls}>Amenities</span>
                            <div className="pt-[4px]"><FieldValue value={hoaAmenities} /></div>
                          </div>
                        </div>
                      )}

                      {hasSchools && (
                        <div className="grid grid-cols-3 gap-x-[24px]">
                          {[
                            { level: 'Elementary School', name: schools.elementary, rate: schools.elementary_state_rate },
                            { level: 'Middle School', name: schools.middle, rate: schools.middle_state_rate },
                            { level: 'High School', name: schools.high, rate: schools.high_state_rate },
                          ].map(({ level, name, rate }) => {
                            // A named school with no rating shows a grey 0/10 badge (26.08 mail); `||`
                            // so '', NaN, null all become 0 before display ("NaN/10" otherwise). A
                            // missing level is stored as '' and gets no badge at all.
                            const displayRate = rate || 0;
                            return (
                              <div key={level}>
                                <span className={labelSchoolCls}>{level}</span>
                                <div className="flex items-center gap-x-[8px]">
                                  {name && (
                                    <span
                                      className="w-[40px] h-[40px] rounded-full flex items-center justify-center text-white font-bold shrink-0"
                                      style={{ backgroundColor: getSchoolRatingColor(displayRate) }}
                                    >
                                      <span style={{ fontSize: 16 }}>{displayRate}</span>
                                      <span style={{ fontSize: 11, fontWeight: 400 }}>/10</span>
                                    </span>
                                  )}
                                  <FieldValue value={name} />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* History tab */}
            {historyTab && (
              <div className="mt-[22px] flex-1 min-h-0 flex flex-col">
                {/* Table header — sticky, not in scroll area */}
                <div className="shrink-0 flex gap-[16px] py-[4px] bg-white rounded-t-[4px]">
                  <div className="w-[224px] flex items-center pl-[12px]">
                    <span className="text-[14px] font-bold text-brand-dark">Date</span>
                  </div>
                  <div className="w-[224px] flex items-center">
                    <span className="text-[14px] font-bold text-brand-dark"> Status</span>
                  </div>
                  <div className="w-[127px] flex items-center pr-[12px]">
                    <span className="text-[14px] font-bold text-brand-dark">Price</span>
                  </div>
                </div>

                {/* Scrollable body */}
                <div className="history-scroll flex-1 min-h-0 max-h-[400px] canvas:max-h-none">
                  {sortedYears.length === 0 && (
                    <p className="text-[13px] text-brand-unselectedGray py-[20px] pl-[12px]">No history available.</p>
                  )}
                  {sortedYears.map(year => (
                    <div key={year}>
                      {groupedHistory[year].map((entry, i) => {
                        const Icon = STATUS_ICON_MAP[entry.event_name]
                        const badgeBg = STATUS_BADGE_BG[entry.event_name]
                        return (
                          <div
                            key={i}
                            className="flex gap-[16px] items-center h-[52px] border-b border-brand-rowBorder"
                          >
                            <div className="w-[224px] pl-[12px] py-[2px] shrink-0">
                              <span className="text-[14px] text-brand-dark">{formatHistoryDate(entry.date)}</span>
                            </div>
                            <div className="w-[224px] flex items-center gap-[8px] shrink-0">
                              {Icon && (
                                badgeBg ? (
                                  <div className="size-[20px] rounded-full flex items-center justify-center shrink-0" style={{ background: badgeBg }}>
                                    <Icon />
                                  </div>
                                ) : (
                                  <Icon className="size-[20px] shrink-0" />
                                )
                              )}
                              <span className="text-[14px] text-brand-dark">
                                {STATUS_LABEL_MAP[entry.event_name] ?? entry.event_name ?? ''}
                              </span>
                            </div>
                            <div className="w-[127px] pr-[12px] py-[2px] shrink-0">
                              <span className="text-[14px] text-brand-dark">
                                {entry.price ? formatPrice(entry.price) : ''}
                              </span>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-col" style={{ flex: '1.2', minWidth: 0 }}>

            {/* Notes card, A3 d: frame property-dashboard/quick-actions, 18 + 20 + 10 + 388 + 6 + 16 + 22 = 480.
                Major Systems left the UI here; details.roof/ac/water_heater/furnace stay in the data. */}
            <div className="flex-1 min-h-[480px] canvas:h-[480px] canvas:flex-none bg-white border border-brand-fieldBdr rounded-[8px] px-[23px] pt-[17px] pb-[21px]">
              <p className="text-[16px] leading-[20px] font-extrabold text-brand-dark mb-[10px]">Notes</p>
              <textarea
                className="block w-full h-[388px] text-[14px] leading-[18px] text-brand-dark border border-brand-fieldBdr rounded-[4px] px-[8px] pt-[12px] pb-[8px] bg-white outline-none hover:border-brand-green focus:border-brand-green resize-none placeholder:text-brand-placeholder"
                value={notes}
                onChange={e => setNotes(e.target.value)}
                onBlur={() => {
                  // setNotesModified only on save's own success -- this line running unconditionally,
                  // regardless of whether the PATCH actually succeeded, was the whole defect: a failed
                  // save still advanced "Last Modified" and read as a successful one.
                  save({ 'notes.text': notes }).then((ok) => {
                    if (ok) setNotesModified(new Date().toISOString());
                  });
                }}
                placeholder="Add notes…"
              />
              {/* Fixed 16 so the card does not shift while there is no date yet. */}
              <span className="text-[12px] leading-[16px] text-brand-secondary mt-[6px] block h-[16px]">
                {
                  notesModified && `Last Modified: ${formatDate(notesModified)}`
                }
              </span>
            </div>

          </div>
        </div>
      </div>
    </PageShell>
  );
};

export default PropertyDashboard;
